import { uiText } from "@/lib/i18n/uiText";
import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { usePredictionReviews } from "@/hooks/usePredictionReviews";
import { useToast } from "@/hooks/use-toast";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  prediction: { id: string; team_home?: string | undefined; team_away?: string | undefined; market_type?: string } | null;
}

export function PredictionReviewDialog({ open, onOpenChange, prediction }: Props) {
  const { submitReview } = usePredictionReviews();
  const { toast } = useToast();
  const [madeSense, setMadeSense] = useState(true);
  const [wouldBet, setWouldBet] = useState(true);
  const [reasoningClear, setReasoningClear] = useState(true);
  const [confidence, setConfidence] = useState(3);
  const [comment, setComment] = useState("");

  const handleSubmit = async () => {
    if (!prediction) return;
    try {
      await submitReview.mutateAsync({
        prediction_id: prediction.id,
        made_sense: madeSense,
        would_bet: wouldBet,
        reasoning_clear: reasoningClear,
        confidence_rating: confidence,
        comment,
      });
      toast({ title: "Review submitted", description: "+20 points earned. Thank you!" });
      onOpenChange(false);
      setComment("");
    } catch (err) {
      toast({ variant: "destructive", title: "Could not submit", description: (err as Error).message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{uiText("Review prediction")}</DialogTitle>
        </DialogHeader>
        {prediction && (
          <p className="text-sm text-muted-foreground">
            {prediction.team_home}  {uiText("vs")} {prediction.team_away} — {uiText(prediction.market_type)}
          </p>
        )}
        <div className="space-y-4 py-2">
          <Toggle label={uiText("Did this prediction make sense?")} value={madeSense} onChange={setMadeSense} />
          <Toggle label={uiText("Would you place this bet?")} value={wouldBet} onChange={setWouldBet} />
          <Toggle label={uiText("Was the reasoning clear?")} value={reasoningClear} onChange={setReasoningClear} />
          <div className="space-y-2">
            <Label className="text-sm">{uiText("Rate your confidence:")} {confidence}/5</Label>
            <Slider value={[confidence]} onValueChange={([v]) => setConfidence(v!)} min={1} max={5} step={1} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">{uiText("Comment (optional)")}</Label>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={submitReview.isPending}>
            {submitReview.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{uiText("Submitting...")}</> : uiText("Submit review (+20 pts)")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <Label className="text-sm">{label}</Label>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
  );
}
